import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import { getEnquiryPaymentTerms, getInquiryActivityLogs, getInquiryById } from "../api/inquiry.api";
import { VERTICALS } from "../../../../constants/verticals";

const InquiryView: React.FC = () => {
    const { id, vertical } = useParams<{ id: string; vertical: string }>();

    const verticalConfig = VERTICALS.find(v => v.key === vertical);
    const verticalId = verticalConfig?.id;
    const verticalName = verticalConfig?.label;

    const isLixil = Number(verticalId) === 3;

    const navigate = useNavigate();

    const [inquiry, setInquiry] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activityLogs, setActivityLogs] = useState<any[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [paymentTermsData, setPaymentTermsData] = useState<any>(null);
    useEffect(() => {
        if (!verticalId || !verticalName) {
            navigate("/dashboard", { replace: true });
        }
    }, [verticalId, verticalName, navigate]);

    useEffect(() => {
        if (id) {
            const inquiryId = Number(id);
            fetchInquiry(inquiryId);
            fetchActivityLogs(inquiryId);
            fetchPaymentTerms(inquiryId);   // ✅ ADD
        }
    }, [id]);
    const formatDate = (date?: string | null) =>
        date ? new Date(date).toLocaleDateString() : "—";

    const formatStatus = (status: string) =>
        status === "in_progress" ? "In Progress" : "Completed";

    const fetchInquiry = async (inquiryId: number) => {
        try {
            setLoading(true);
            const res = await getInquiryById(inquiryId);
            setInquiry(res.data.data);
        } catch (error) {
            console.error("Failed to load inquiry", error);
        } finally {
            setLoading(false);
        }
    };
    const fetchPaymentTerms = async (inquiryId: number) => {
        try {
            const res = await getEnquiryPaymentTerms(inquiryId);
            setPaymentTermsData(res.data);
        } catch (err) {
            console.error("Failed to fetch payment terms", err);
        }
    };
    const fetchActivityLogs = async (inquiryId: number) => {
        try {
            setLoadingLogs(true);

            const res = await getInquiryActivityLogs(inquiryId);
            const logs = res.data.data || [];

            setActivityLogs(
                logs.sort(
                    (a: any, b: any) =>
                        new Date(b.date_created).getTime() -
                        new Date(a.date_created).getTime()
                )
            );
        } catch (error) {
            console.error("Failed to fetch activity logs", error);
        } finally {
            setLoadingLogs(false);
        }
    };
    if (loading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    if (!inquiry) {
        return <div className="text-center mt-5">Inquiry not found</div>;
    }

    return (
        <>
            <PageHeader
                title={`View ${verticalName ?? "Inquiry"}`}
                breadcrumbs={[
                    { label: "Dashboard", path: "/dashboard" },
                    { label: "Sales" },
                    { label: verticalName ?? "" },
                    { label: "View Inquiry" },
                ]}
            />


            <div className="container mt-2">
                <div className="card common-form-card p-3">

                    {/* ================= CLIENT DETAILS ================= */}
                    <Section title="Client Details">
                        <Info
                            label="Client Organization Name"
                            value={inquiry.customer_name}
                        />
                        <Info
                            label="Contact Details"
                            value={inquiry.contact_person_name}
                        />
                        <Info
                            label="Contact Segment"
                            value={inquiry.client_segment_name}
                        />
                    </Section>

                    {/* ================= PROJECT DETAILS ================= */}
                    <Section title="Project Details">
                        {!isLixil && (
                            <Info
                                label="Project Name"
                                value={inquiry.enquiry_title}
                            />
                        )}

                        <Info
                            label="PL"
                            value={inquiry.platform_name}
                        />
                        <Info
                            label="Client Architect Name"
                            value={inquiry.architect_name || "—"}
                        />
                        {!isLixil && (
                            <Info
                                label="Facade Consultant"
                                value={inquiry.facade_consultant || "—"}
                            />
                        )}

                        <Info
                            label="Channel"
                            value={inquiry.channel_name}
                        />

                        <Info label="State" value={inquiry.state} />
                        <Info label="City" value={inquiry.city} />
                        <Info label="Area" value={inquiry.area} />
                        <Info label="Pincode" value={inquiry.pincode} />

                        <Info
                            label="Expected Closing Forecast Month"
                            value={inquiry.expected_closing_date || "—"}
                        />
                    </Section>




                    {/* ================= OTHER DETAILS ================= */}
                    <Section title="Other Details">
                        <Info
                            label="Inquiry Source"
                            value={inquiry.source_name}
                        />
                        <Info
                            label="Inquiry Stage"
                            value={inquiry.enquiry_stage_name}
                        />
                        <Info label="Responsible Person" value={inquiry.owner_name} />

                        <Info
                            label="Assign To"
                            value={inquiry.assigned_to_name}
                        />

                        <Info
                            label="Inquiry Received Date"
                            value={
                                inquiry.inquiry_received_date
                                    ? new Date(
                                        inquiry.inquiry_received_date
                                    ).toLocaleDateString()
                                    : "—"
                            }
                        />
                        <Info
                            label="Special Note"
                            value={inquiry.remark || "—"}
                        />
                    </Section>

                    <Section title="Documents">
                        {inquiry.documents_data && inquiry.documents_data.length > 0 ? (
                            inquiry.documents_data.map((doc: any) => {
                                const fileName = doc.document_url.split("/").pop();

                                return (
                                    <div className="taiga-attachment-row" key={doc.id}>
                                        {/* Left: file info */}
                                        <div className="taiga-file-info">
                                            <i className="fas fa-paperclip me-2"></i>
                                            <div>
                                                <div className="fw-semibold">{fileName}</div>
                                                <small className="text-muted">
                                                    Uploaded by {doc.uploaded_by_name}
                                                </small>
                                                <div className="small text-muted">
                                                    {new Date(doc.date_created).toLocaleString()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Middle: description (future-safe) */}
                                        <div className="taiga-desc-area">
                                            <span className="text-muted small">
                                                {doc.description || "No description"}
                                            </span>
                                        </div>

                                        {/* Right: view action only */}
                                        <div className="taiga-actions">
                                            <a
                                                href={doc.document_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-link btn-sm"
                                                title="View document"
                                                style={{ color: "var(--primary-color)" }}
                                            >
                                                <i className="fas fa-eye fs-5"></i>
                                            </a>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-12 text-muted">No documents uploaded</div>
                        )}
                    </Section>
                    {inquiry.enquiry_stage_name?.toLowerCase().includes("won") && paymentTermsData && (
                        <div className="inquiry-card mt-4 mb-4">

                            {/* Header */}
                            <div className="inquiry-card-header">
                                <i className="fas fa-money-check-alt"></i>
                                <span className="ms-2">Payment Terms</span>
                            </div>

                            <div className="inquiry-card-body p-0">

                                {/* Won Amount Row */}
                                <div className="p-3 border-bottom bg-light d-flex justify-content-between">
                                    <span className="fw-semibold">Quotation Amount</span>

                                    <span className="badge bg-success fs-6">
                                        ₹{paymentTermsData.quotation_amount?.toLocaleString()}
                                    </span>
                                </div>

                                {/* Payment Terms Table */}
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover mb-0">

                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "80px" }}>#</th>
                                                <th>Payment Term</th>
                                                <th style={{ width: "150px" }}>Amount</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {paymentTermsData.payment_terms?.length ? (
                                                paymentTermsData.payment_terms.map(
                                                    (term: any, index: number) => (
                                                        <tr key={term.term_id}>
                                                            <td>{index + 1}</td>
                                                            <td>{term.name}</td>
                                                            <td className="fw-semibold">
                                                                ₹{Number(term.amount).toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    )
                                                )
                                            ) : (
                                                <tr>
                                                    <td colSpan={2} className="text-center py-3">
                                                        No payment terms found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>

                                    </table>
                                </div>

                            </div>
                        </div>
                    )}
                    {/* ================= ACTIVITY LOG ================= */}
                    <Section title="Activity Log">

                        <div className="col-12 p-0">
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover mb-0">

                                    <thead className="table-light">
                                        <tr>
                                            <th>Stage</th>
                                            <th>Comment</th>
                                            <th>Scheduled Activity</th>
                                            <th>Created By</th>
                                            <th>Assigned To</th>
                                            <th>Value</th>
                                            <th>Created Date</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {loadingLogs ? (
                                            <tr>
                                                <td colSpan={8} className="text-center py-3">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : activityLogs.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center py-3">
                                                    No activity logs found
                                                </td>
                                            </tr>
                                        ) : (
                                            activityLogs.map((log: any) => (
                                                <tr key={log.id}>
                                                    <td>{log.stage_name}</td>
                                                    <td>{log.comment || "—"}</td>
                                                    <td>{formatDate(log.scheduled_activity_date)}</td>
                                                    <td>{log.created_by_name}</td>
                                                    <td>{log.activity_assigned_to_name}</td>
                                                    <td>{log.value ?? "—"}</td>
                                                    <td>{formatDate(log.date_created)}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${log.status === "in_progress"
                                                                ? "bg-warning text-dark"
                                                                : "bg-success"
                                                                }`}
                                                        >
                                                            {formatStatus(log.status)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}

                                    </tbody>

                                </table>
                            </div>
                        </div>

                    </Section>

                    {/* ================= FOOTER ================= */}
                    <div className="d-flex justify-content-end mt-4">
                        <button
                            className="btn btn-secondary"
                            onClick={() =>
                                navigate(`/${vertical}/inquiries`)
                            }
                        >
                            Back
                        </button>
                    </div>

                </div>
            </div>
        </>
    );
};

export default InquiryView;

/* ================= SMALL HELPERS ================= */

const Section = ({ title, children }: any) => (
    <div className="form-section">
        <div className="form-section-header bg-primary text-white mb-0">
            {title}
        </div>
        <div className="row g-3 p-3">
            {children}
        </div>
    </div>
);

const Info = ({ label, value }: { label: string; value: string }) => (
    <div className="col-md-3">
        <div className="text-muted small">{label}</div>
        <div className="fw-semibold">{value}</div>
    </div>
);
